import React, { useEffect, useState, useRef } from 'react';
import useGameBase from '../../hooks/useGameBase';

export default function TimeTapGame({ roomId, isHost, gameState, gamePlayers = [], user = {}, onGameAction }) {
  const { submitAction } = useGameBase({ gameState, gamePlayers, user, roomId });
  const [submitted, setSubmitted] = useState(false);
  const [countdownLabel, setCountdownLabel] = useState(null); // null | '3'|'2'|'1'|'Go'
  const localStartRef = useRef(null);
  const previousCountdownRef = useRef(null);
  const [localElapsedMs, setLocalElapsedMs] = useState(null);
  const [submittedElapsedMs, setSubmittedElapsedMs] = useState(null);
  const countdownRef = useRef(null);
  const intervalRef = useRef(null);

  const phase = gameState ? gameState.phase : 'waiting';
  const targetSeconds = (gameState && gameState.targetSeconds) || 5;
  const timerFadesAway = typeof (gameState && gameState.timerFadesAway) !== 'undefined' ? !!gameState.timerFadesAway : true;

  useEffect(() => {
    // clear state when phase changes
    setSubmitted(false);
    //setSubmittedElapsedMs(null);
    // reset countdown when phase changes
    setCountdownLabel(null);
    // reset local timer start when a new phase begins
    localStartRef.current = null;
  // only show elapsed once Go appears; don't pre-fill 0ms at the start of counting
  setLocalElapsedMs(null);
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
    const id = intervalRef.current;
    return () => {
      if (id) clearInterval(id);
    };
    // NOTE: depend only on phase; depending on full gameState caused the timer to reset
    // whenever any small state update (like our own submit) arrived from the server.
  }, [phase]);

  const startRound = async () => {
    try {
      const action = { type: 'startRound' };
      if (typeof onGameAction === 'function') onGameAction(action);
      else await submitAction(action);
    } catch (e) {
      console.error('startRound failed', e);
    }
  };

  const submit = async () => {
    if (submitted) return;
    // disable immediately to prevent duplicate clicks
    setSubmitted(true);
    try {
      // compute client-side elapsedMs since localStartRef (Go moment on this client)
      let elapsedMs = null;
      if (localStartRef.current != null) {
        try { elapsedMs = Math.max(0, performance.now() - localStartRef.current); } catch (e) { elapsedMs = Math.max(0, Date.now() - (localStartRef.current || Date.now())); }
      }
      // keep a temporary copy so the UI can show the exact time the user clicked
      setSubmittedElapsedMs(elapsedMs);
      console.log('TimeTap submit', { elapsedMs, localStartRef: localStartRef.current });
      const action = { type: 'submit', elapsedMs };
      if (typeof onGameAction === 'function') onGameAction(action);
      else await submitAction(action);
      // keep submitted = true on success
    } catch (e) {
      console.error('submit failed', e);
      // re-enable button on failure so user can retry
      setSubmitted(false);
      setSubmittedElapsedMs(null);
    }
  };

  // Start a simple 3-2-1-Go countdown when counting starts.
  useEffect(() => {
    // Drive a requestAnimationFrame loop to update the countdown label and the
    // high-resolution elapsed timer. We set localStartRef exactly when the
    // countdown label transitions to 'Go' so elapsedMs starts at that instant.
    let rafId = null;
    const update = () => {
      try {
        if (phase !== 'counting' || !gameState) {
          setCountdownLabel(null);
          previousCountdownRef.current = null;
          return;
        }
        const now = Date.now();
        const startAt = gameState.roundStartAt || null;
        if (!startAt) {
          setCountdownLabel(null);
          previousCountdownRef.current = null;
          return;
        }
        const msUntilStart = startAt - now;
        let newLabel = null;
        if (msUntilStart > 2000) newLabel = '3';
        else if (msUntilStart > 1000) newLabel = '2';
        else if (msUntilStart > 0) newLabel = '1';
        else newLabel = 'Go';

        // update label only when it changes to reduce re-renders
        if (previousCountdownRef.current !== newLabel) {
          setCountdownLabel(newLabel);
        }

        // If we've just transitioned into 'Go', initialize the precise start time
        // We keep the internal countdownLabel='Go' for timing purposes but do
        // not render 'Go' visually (the UI will hide it). This ensures the
        // local timer starts at the exact instant the server intended.
        if (newLabel === 'Go' && previousCountdownRef.current !== 'Go') {
          if (!localStartRef.current) {
            try { localStartRef.current = performance.now(); } catch (e) { localStartRef.current = Date.now(); }
            // initialize visible elapsed at the moment Go occurs
            setLocalElapsedMs(0);
          }
        }

        // While in Go, update the live elapsed ms if we have a start time
        // and the player hasn't already submitted. Once submitted=true we
        // freeze the displayed elapsed ms.
        if (newLabel === 'Go' && localStartRef.current != null && !submitted) {
          try {
            setLocalElapsedMs(Math.max(0, Math.round(performance.now() - localStartRef.current)));
          } catch (e) {
            setLocalElapsedMs(Math.max(0, Date.now() - (localStartRef.current || Date.now())));
          }
        }

        previousCountdownRef.current = newLabel;
      } catch (e) {
        setCountdownLabel(null);
        previousCountdownRef.current = null;
      }
      rafId = requestAnimationFrame(update);
    };
    update();
    return () => {
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [phase, gameState, submitted]);

  // compute time left / progress
  // we keep gameState timestamps accessible if needed; simplified UI doesn't compute progress here

  // Only render a single large round button as requested.
  // Button label: "Press after {targetSeconds}".
  // Behavior:
  // - If phase === 'waiting' and user is host: clicking starts the round (startRound())
  // - If phase === 'counting': clicking submits (submit())
  // - Otherwise clicking does nothing.
  // Button should only be enabled when countdown reached 'Go' during counting.
  // Enable only when the internal countdown reached Go (even if Go is hidden)
  const countingEnabled = phase === 'counting' && countdownLabel === 'Go' && !submitted;
  const buttonDisabled = !(countingEnabled || (phase === 'waiting' && isHost));

  // Determine current round and total rounds
  const currentRound = typeof gameState?.round === 'number' && gameState.round > 0 ? gameState.round : 1;
  const totalRounds = typeof gameState?.maxRounds === 'number' ? gameState.maxRounds : (typeof gameState?.rounds === 'number' ? gameState.rounds : null);

  return (
    <div className="timetap-game flex items-center justify-center h-full">
      <div className="flex flex-col items-center w-full">
        {/* Show round indicator at the top */}
        {totalRounds && (
          <div className="mb-4 text-lg font-bold text-center text-cyan-700 dark:text-cyan-200">
            Round {currentRound}/{totalRounds}
          </div>
        )}
        
        <button
        onClick={() => {
          if (phase === 'waiting' && isHost) startRound();
          else if (phase === 'counting') {
            
            submit();}
        }}
        disabled={buttonDisabled}
        aria-label={`Press after ${targetSeconds} seconds`}
        className={`relative flex items-center justify-center select-none focus:outline-none disabled:opacity-75 transition-transform duration-150 ease-out rounded-full
          ${buttonDisabled ? 'bg-gray-400 dark:bg-gray-600' : 'bg-green-600 dark:bg-green-500 hover:scale-105 cursor-pointer'} text-white font-semibold${buttonDisabled ? ' cursor-not-allowed' : ''}`}
        style={{ width: 220, height: 220, fontSize: 20, overflow: 'hidden', boxShadow: buttonDisabled ? 'inset 0 -6px 10px rgba(0,0,0,0.15)' : '0 18px 30px rgba(0,0,0,0.35), inset 0 -6px 12px rgba(0,0,0,0.18)', backgroundImage: buttonDisabled ? 'linear-gradient(#d1d5db,#9ca3af)' : 'radial-gradient(circle at 30% 25%, rgba(255,255,255,0.18), rgba(255,255,255,0.04) 20%), linear-gradient(#34d399,#059669)'}}
        onMouseDown={(e) => { if (!buttonDisabled) e.currentTarget.style.transform = 'translateY(4px) scale(0.98)'; }}
        onMouseUp={(e) => { e.currentTarget.style.transform = ''; }}
        onMouseLeave={(e) => { e.currentTarget.style.transform = ''; }}
      >
        

        {(phase !== 'counting' || submitted) && (submittedElapsedMs != null || localElapsedMs != null) ? (
          (() => {
            const displayMs = submittedElapsedMs != null ? submittedElapsedMs : localElapsedMs;
            const rounded = displayMs == null ? '—' : Math.round(displayMs);
            const delta = displayMs == null ? null : Math.round(displayMs - (targetSeconds * 1000));
            const signed = delta == null ? '' : (delta >= 0 ? `+${delta}` : `${delta}`) + ' ms';
            return (
              <div className="text-center">
                <div className="text-lg font-bold text-gray-900 dark:text-gray-900">{rounded} ms</div>
                <div className="text-sm text-gray-700 dark:text-gray-700">{signed}</div>
              </div>
            );
          })()
        ) : (
          <div className="text-center">Press after <br />{targetSeconds} seconds</div>
        )}
        </button>
        {/* Live client-side elapsed milliseconds shown during Go */}
        <div
          className={`text-4xl mt-3 text-gray-700 dark:text-gray-300 transition-opacity duration-1000 ${
              timerFadesAway && localElapsedMs != null && localElapsedMs >= 1000 ? 'opacity-0' : 'opacity-100'
            }`}
          aria-hidden
        >
          {localElapsedMs != null ? `${localElapsedMs} ms` : ''}
        </div>
        <div className={`mb-4 text-4xl font-bold text-center text-gray-800 dark:text-gray-200`} aria-hidden>
          {/* Show the numeric countdown (3,2,1) but do not display 'Go' visually */}
          {['3','2','1'].includes(countdownLabel) ? countdownLabel : (phase === 'waiting' && isHost ? 'Ready' : '')}
        </div>
        {/* Show results after a round finishes */}
        {phase === 'results' && gameState && Array.isArray(gameState.results) && gameState.results.length > 0 && (
          <div className="mb-6 w-full max-w-md mx-auto rounded-3xl border border-cyan-200/70 bg-gradient-to-br from-cyan-50 via-white to-amber-50 shadow-[0_24px_70px_rgba(15,23,42,0.12)] dark:border-cyan-900/60 dark:from-slate-950 dark:via-slate-900 dark:to-cyan-950/80 p-0">
            <div className="flex items-center justify-between px-6 pt-5 pb-2">
              <div className="text-xs font-bold uppercase tracking-[0.24em] text-cyan-700 dark:text-cyan-200">RESULTS</div>
              <div className="font-semibold text-base text-slate-900 dark:text-white">Round {typeof gameState.round === 'number' && gameState.round > 0 ? gameState.round : 1}</div>
            </div>
            <div className="px-6 pb-6">
              {(() => {
                const last = gameState.results[gameState.results.length - 1];
                return (
                  <div className="space-y-2">
                    {(() => {
                      // sort players by closeness to the target (smallest offset first)
                      const sorted = Array.isArray(last.perPlayer) ? [...last.perPlayer].sort((a, b) => {
                        const oa = typeof a.offsetMs === 'number' ? a.offsetMs : Number.POSITIVE_INFINITY;
                        const ob = typeof b.offsetMs === 'number' ? b.offsetMs : Number.POSITIVE_INFINITY;
                        return oa - ob;
                      }) : [];

                      return sorted.map((p) => {
                        const isWinner = last.winner === p.socketId;
                        const failed = !!p.failed;
                        const elapsed = typeof p.elapsedMs === 'number' ? Math.round(p.elapsedMs) : null;
                        const offset = typeof p.offsetMs === 'number' ? Math.round(p.offsetMs) : null;
                        // compute signed delta relative to targetSeconds
                        const delta = elapsed != null ? Math.round(elapsed - (targetSeconds * 1000)) : null;
                        const signed = delta == null ? '—' : (delta >= 0 ? `+${delta}` : `${delta}`) + ' ms';
                        const compliment = offset != null && Math.abs(offset) <= 100 ? 'Nice!' : '';

                        return (
                          <div
                            key={p.socketId}
                            className={`flex justify-between items-center text-sm py-2 px-3 rounded-2xl transition-all duration-300 ${failed ? 'text-gray-400 dark:text-gray-500' : 'text-gray-800 dark:text-gray-200'} ${isWinner && !failed ? 'bg-emerald-100 dark:bg-emerald-900/60' : 'bg-white/70 dark:bg-slate-900/60'}`}
                          >
                            <div className="truncate mr-2 font-medium">{p.username || p.socketId}{isWinner && !failed ? ' ⭐' : ''}</div>
                            <div className="text-right">
                              {failed ? (
                                <span className="italic">No response</span>
                              ) : (
                                <span>
                                  {elapsed != null ? `${elapsed} ms` : '—'}
                                  <span className="ml-2">(Δ {signed})</span>
                                  {compliment ? <span className="ml-2 font-semibold text-green-700 dark:text-green-300">{compliment}</span> : null}
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      });
                    })()}
                  </div>
                );
              })()}
            </div>
          </div>
        )}
        {1==2 && 
        (
        <div className="mt-6 w-full max-w-md p-3 bg-gray-100 dark:bg-gray-900 rounded border">
          <div className="font-semibold mb-2">Debug: timing</div>
          <div className="text-xs text-gray-700 dark:text-gray-300">phase: {phase}</div>
          <div className="text-xs text-gray-700 dark:text-gray-300">roundStartAt: {gameState && gameState.roundStartAt ? new Date(gameState.roundStartAt).toISOString() : 'null'}</div>
          <div className="text-xs text-gray-700 dark:text-gray-300">roundTargetAt: {gameState && gameState.roundTargetAt ? new Date(gameState.roundTargetAt).toISOString() : 'null'}</div>
          <div className="text-xs text-gray-700 dark:text-gray-300">localStartRef: {localStartRef.current != null ? Math.round(localStartRef.current) : 'null'}</div>
          <div className="text-xs text-gray-700 dark:text-gray-300">localElapsedMs: {localElapsedMs != null ? `${localElapsedMs} ms` : 'null'}</div>
          <div className="text-xs text-gray-700 dark:text-gray-300">countdownLabel: {countdownLabel || 'null'}</div>
          <div className="mt-2 text-xs text-gray-700 dark:text-gray-300">Submissions:</div>
          <div className="text-xs text-gray-600 dark:text-gray-400">
            {gameState && gameState.submissions && Object.keys(gameState.submissions).length > 0 ? (
              <div>
                {Object.entries(gameState.submissions).map(([sid, sub]) => (
                  <div key={sid} className="flex justify-between">
                    <div className="truncate mr-2">{sid}</div>
                    <div>{sub.elapsedMs != null ? `${sub.elapsedMs} ms` : 'null'} @ {sub.timeMs ? new Date(sub.timeMs).toISOString() : 'null'}</div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-xs italic">(no submissions yet)</div>
            )}
          </div>
          <div className="mt-2 text-xs text-gray-700 dark:text-gray-300">Last result:</div>
          <div className="text-xs text-gray-600 dark:text-gray-400">
            {gameState && Array.isArray(gameState.results) && gameState.results.length > 0 ? (
              <pre className="whitespace-pre-wrap text-xs">{JSON.stringify(gameState.results[gameState.results.length - 1], null, 2)}</pre>
            ) : (
              <div className="text-xs italic">(no results yet)</div>
            )}
          </div>
        </div>
        )}
      </div>
    </div>
  );
}

TimeTapGame.Guide = function TimeTapGuide() {
  return (
    <div>
      <h3 className="font-bold mb-2">Time Tap</h3>
      <p className="mb-1">Press the button after the target number of seconds have elapsed. Closest to the target wins points for that round.</p>
    </div>
  );
};
